import React from 'react'
import { Button, Card, Input, message, Space, Switch, Typography } from 'antd'
import {
    EnvironmentOutlined,
    AppstoreOutlined,
    ShopOutlined,
    ApartmentOutlined,
    CarOutlined,
    DollarOutlined,
    ShoppingCartOutlined,
    UserOutlined,
    SafetyOutlined,
    FileTextOutlined,
    WalletOutlined,
    SettingOutlined,
    BarChartOutlined,
    ToolOutlined,
    BellOutlined,
    IdcardOutlined,
    ExportOutlined
} from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { apiUrl } from '../../config/api'

export default function AdminHome() {
    const [secret, setSecret] = React.useState('')
    const [input, setInput] = React.useState('')
    const [credit, setCredit] = React.useState(null)
    const [creditLoading, setCreditLoading] = React.useState(false)

    const [systemTime, setSystemTime] = React.useState(null)
    const [timeLoading, setTimeLoading] = React.useState(false)

    const [showIds, setShowIds] = React.useState(() => {
        try { return localStorage.getItem('showIds') === 'true' } catch { return false }
    })

    document.title = 'Admin';


    // Load current secret and prompt only if not set on entering /admin
    React.useEffect(() => {
        try {
            const current = localStorage.getItem('x-auth-secret') || ''
            setSecret(current)
            setInput(current)
            if (!current) {
                // Prompt first-time users to enter the secret
                const entered = window.prompt('Enter x-auth-secret:')
                if (entered !== null) {
                    const trimmed = (entered || '').trim()
                    if (trimmed) {
                        localStorage.setItem('x-auth-secret', trimmed)
                        setSecret(trimmed)
                        setInput(trimmed)
                        message.success('x-auth-secret saved')
                    }
                }
            }
        } catch {
            // ignore storage errors
        }
    }, [])

    async function loadCredit() {
        setCreditLoading(true)
        try {
            const resp = await fetch(apiUrl('/api/users/otp/credit'))
            if (!resp.ok) throw new Error('failed')
            const txt = await resp.text()
            setCredit(txt)
        } catch (e) {
            console.error(e)
            message.error('فشل في جلب رصيد OTP')
        } finally {
            setCreditLoading(false)
        }
    }

    async function loadSystemTime() {
        setTimeLoading(true)
        try {
            const headers = { 'Accept': 'application/json' }
            try {
                const s = localStorage.getItem('x-auth-secret')
                if (s) headers['x-auth-secret'] = s
            } catch { /* ignore */ }
            const resp = await fetch(apiUrl('/api/admin/system/time'), { headers, credentials: 'include' })
            if (!resp.ok) throw new Error('failed')
            const json = await resp.json()
            setSystemTime(json)
        } catch (e) {
            console.error(e)
            message.error('فشل في جلب توقيت النظام')
        } finally {
            setTimeLoading(false)
        }
    }

    React.useEffect(() => {
        if (secret) {
            loadCredit()
            loadSystemTime()
        }
    }, [secret])

    function saveSecret() {
        const val = (input || '').trim()
        try {
            if (!val) {
                localStorage.removeItem('x-auth-secret')
                setSecret('')
                message.info('x-auth-secret cleared')
            } else {
                localStorage.setItem('x-auth-secret', val)
                setSecret(val)
                message.success('x-auth-secret saved')
            }
        } catch {
            message.error('Failed to save secret')
        }
    }

    function toggleShowIds(checked) {
        try {
            localStorage.setItem('showIds', checked ? 'true' : 'false')
            setShowIds(!!checked)
        } catch (e) { console.error(e) }
    }

    function clearSecret() {
        try {
            localStorage.removeItem('x-auth-secret')
            setSecret('')
            setInput('')
            message.info('x-auth-secret cleared')
        } catch {
            message.error('Failed to clear secret')
        }
    }

    const data = [
        { key: 'orders', title: 'الطلبات', icon: <ShoppingCartOutlined /> },
        { key: 'shop-dues', title: 'مستحقات المتاجر', icon: <WalletOutlined /> },
        { key: 'financial-records', title: 'السجلات المالية', icon: <FileTextOutlined /> },
        { key: 'stats', title: 'الإحصائيات', icon: <BarChartOutlined /> },
        { key: 'shops', title: 'المتاجر', icon: <ShopOutlined /> },
        { key: 'shop-item-maps', title: 'تسعير عناصر المتجر', icon: <DollarOutlined /> },
        { key: 'shops-tags', title: 'التصنيفات الرئيسية', icon: <AppstoreOutlined /> },
        { key: 'shop-tag-maps', title: 'المتجر ← التصنيف الرئيسي', icon: <ApartmentOutlined /> },
        { key: 'delivery-locations', title: 'مناطق التوصيل', icon: <EnvironmentOutlined /> },
        { key: 'shop-location-fees', title: 'رسوم التوصيل: متجر ← منطقة', icon: <CarOutlined /> },
        { key: 'services', title: 'الخدمات وربط المناطق', icon: <ToolOutlined /> },
        { key: 'captains', title: 'إدارة الكباتن', icon: <IdcardOutlined /> },
        { key: 'off-platform-orders', title: 'الطلبات الخارجية', icon: <ExportOutlined /> },
        { key: 'admin-users', title: 'المستخدمون', icon: <UserOutlined /> },
        { key: 'admin-user-otps', title: 'أكواد OTP', icon: <SafetyOutlined /> },
        { key: 'push-notifications', title: 'إشعارات التطبيق', icon: <BellOutlined /> },
        { key: 'general-config', title: 'الإعدادات العامة', icon: <SettingOutlined /> }
    ]

    return (
        <div style={{ maxWidth: 900, margin: '40px auto', padding: '0 16px' }}>
            {/* Auth Secret Section */}
            <Card style={{ marginBottom: 16 }}>
                <Typography.Title level={5} style={{ marginTop: 0 }}>x-auth-secret</Typography.Title>
                <Typography.Paragraph type={secret ? 'secondary' : undefined} style={{ marginBottom: 8 }}>
                    يتم حفظ هذه القيمة في المتصفح وستُرسل في ترويسة x-auth-secret لطلبات الخلفية.
                </Typography.Paragraph>
                <Space.Compact style={{ width: '100%' }}>
                    <Input.Password
                        placeholder="أدخل x-auth-secret"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                    />
                    <Button type="primary" onClick={saveSecret}>حفظ</Button>
                    <Button danger onClick={clearSecret}>مسح</Button>
                </Space.Compact>
                <Typography.Paragraph style={{ marginTop: 8, direction: 'ltr' }}>
                    الحالي: {secret ? '•••••••• (تم الضبط)' : '(غير مضبوط)'}
                </Typography.Paragraph>
            </Card>

            {/* Show IDs */}
            <Card style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                    <div>
                        <Typography.Title level={5} style={{ marginTop: 0 }}>Show IDs</Typography.Title>
                        <Typography.Paragraph type="secondary" style={{ margin: '4px 0 0', fontSize: 12 }}>
                            يعرض معرف العنصر (ID) على بطاقات التصنيفات والمتاجر والمنتجات
                        </Typography.Paragraph>
                    </div>
                    <Switch checked={showIds} onChange={toggleShowIds} />
                </div>
            </Card>

            {/* OTP Credit Section */}
            <Card style={{ marginBottom: 16 }}>
                <Typography.Title level={5} style={{ marginTop: 0 }}>الرصيد المتبقي لرسائل OTP</Typography.Title>
                <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button onClick={loadCredit} loading={creditLoading}>تحديث الرصيد</Button>
                    <Typography.Text style={{ direction: 'ltr' }}>
                        {creditLoading ? '...' : (credit ?? '—')}
                    </Typography.Text>
                </Space>
            </Card>

            {/* System Time Section */}
            <Card style={{ marginBottom: 16 }}>
                <Typography.Title level={5} style={{ marginTop: 0 }}>توقيت النظام</Typography.Title>
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Button onClick={loadSystemTime} loading={timeLoading}>تحديث التوقيت</Button>
                    <div style={{ direction: 'ltr', marginTop: 8 }}>
                        <div><Typography.Text strong>Server Time:</Typography.Text> {systemTime?.serverTime || '—'}</div>
                        <div><Typography.Text strong>DB Time:</Typography.Text> {systemTime?.dbTime || '—'}</div>
                    </div>
                </Space>
            </Card>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                    gap: 12,
                    direction: 'rtl'
                }}
            >
                {data.map((item) => (
                    <Link key={item.key} to={`/admin/${item.key}`} style={{ textDecoration: 'none' }}>
                        <Card
                            hoverable
                            size="small"
                            styles={{ body: { padding: 16 } }}
                            style={{ height: '100%' }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 10,
                                    minHeight: 96,
                                    textAlign: 'center'
                                }}
                            >
                                <div style={{ fontSize: 32, color: '#1677ff', lineHeight: 1 }}>
                                    {item.icon}
                                </div>
                                <Typography.Text strong style={{ fontSize: 14, lineHeight: 1.3 }}>
                                    {item.title}
                                </Typography.Text>
                            </div>
                        </Card>
                    </Link>
                ))}
            </div>
        </div>
    )
}
